import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ComponentSync, LL_H_C, ModuleFE_MouseInteractivity, mouseInteractivity_ToolTip } from '@nu-art/thunderstorm-frontend/index';
import { BadImplementationException, exists, formatTimestamp, MissingDataException } from '@nu-art/ts-common';
import './Tooltip_Finding.scss';
import { resolveValueDisplay } from '@app/hcs-shared';
export class Tooltip_Finding_Base extends ComponentSync {
    // `this.props`/`this.state` stay as the merged `P`/`S` so subclasses see their
    // overwritten types (e.g. `data: FindingTT_Vitals_Data`). Inside the base body
    // those generics are unresolved, so the base's own logic reads through these
    // concrete views — OWCombine guarantees every base key is present on `P`/`S`.
    get baseProps() {
        return this.props;
    }
    get baseState() {
        return this.state;
    }
    static hoverTimeDelay = 300;
    hoverTimeout;
    modalPos = { x: 1, y: 1 };
    modalOffset = { x: 5, y: 10 };
    originPosResolver = e => ({ x: e.clientX, y: e.clientY });
    //######################### Life Cycle #########################
    deriveStateFromProps(_nextProps, _state) {
        // View through the base contract; the merged props/state always carry these keys.
        const nextProps = _nextProps;
        const state = _state;
        state.data = nextProps.data;
        state.showSource = !!nextProps.showSource;
        return _state;
    }
    //######################### Logic #########################
    assertChildType = () => {
        const child = this.baseProps.children;
        // Validate that the child can accept mouse events
        if (typeof child.type === 'string' && child.type !== 'div') {
            throw new BadImplementationException('FindingTooltip only accepts a single child of type div or a component returning a <div>');
        }
    };
    onMouseEnter = (e) => {
        const pos = this.originPosResolver(e);
        this.hoverTimeout = setTimeout(() => {
            const model = {
                id: 'finding-tooltip',
                originPos: pos,
                modalPos: this.modalPos,
                content: this.renderTooltip(),
                offset: this.modalOffset,
                contentHoverDelay: 200,
            };
            ModuleFE_MouseInteractivity.showTooltip(model);
            this.baseProps.onTooltipOpened?.();
        }, Tooltip_Finding_Base.hoverTimeDelay);
    };
    onMouseLeave = (e) => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_ToolTip);
        clearTimeout(this.hoverTimeout);
        delete this.hoverTimeout;
    };
    isAbnormal = (result) => {
        if (typeof result.rawValue !== 'number')
            return false;
        const range = this.baseState.data.metadata.varNormal;
        if (!range)
            return false;
        return result.rawValue < range[0] || result.rawValue > range[1];
    };
    //######################### Logic - UI Resolving #########################
    resolveVariableName = (variable) => {
        return variable?.name.includes('.') ? variable?.name.split('.')[1] : variable?.name ?? 'N/A';
    };
    resolveValue = (result) => {
        if (!exists(result.rawValue) && !result.valueId)
            return 'N/A';
        if (exists(result.rawValue))
            return typeof result.rawValue === 'string' ? result.rawValue : result.rawValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
        const value = this.baseProps.modules.value.cache.unique(result.valueId);
        if (!value)
            throw new MissingDataException(`Could not find value for id ${result.valueId}`);
        return resolveValueDisplay(value);
    };
    resolveMeasurementUnits = () => {
        const measurementUnitId = this.baseState.data.metadata?.measurementUnitId;
        return this.baseProps.modules.measurementUnit.cache.unique(measurementUnitId)?.unitAsString;
    };
    resolveSource = () => {
        const source = this.baseState.data.metadata.source;
        switch (source) {
            case 'advisor':
                return 'Advisor';
            case 'fhir':
                return 'FHIR';
            case 'calculator':
                return 'Calculator';
            default:
                return 'N/A';
        }
    };
    resolveTimestamp = (result) => {
        return result.currentEncounter ? formatTimestamp('hh:mmA', result.timestamp) : formatTimestamp('D MMM YYYY', result.timestamp);
    };
    //######################### Render #########################
    render() {
        this.assertChildType();
        return React.cloneElement(this.baseProps.children, {
            onMouseEnter: this.onMouseEnter,
            onMouseLeave: this.onMouseLeave,
        });
    }
    renderTooltip = () => {
        return _jsxs(_Fragment, { children: [this.render_Title(), this.render_Content()] });
    };
    render_Title = () => {
        return _jsx("div", { className: 'finding-tooltip__title', children: this.resolveVariableName(this.baseState.data.variable) });
    };
    render_Source = () => {
        if (!this.baseState.showSource)
            return;
        return _jsxs(LL_H_C, { className: 'finding-tooltip__source', children: [_jsx("div", { className: 'finding-tooltip__source__title', children: "Source:" }), _jsx("div", { className: 'finding-tooltip__source__value', children: this.resolveSource() })] });
    };
    render_NormalRange = () => {
        const normalRange = this.baseState.data.metadata?.varNormal;
        if (!normalRange)
            return;
        const min = normalRange[0].toLocaleString(undefined, { maximumFractionDigits: 2 });
        const max = normalRange[1].toLocaleString(undefined, { maximumFractionDigits: 2 });
        return _jsxs(LL_H_C, { className: 'finding-tooltip__normal-range', children: [_jsx("div", { className: 'finding-tooltip__normal-range__title', children: "Normal:" }), _jsxs("div", { className: 'finding-tooltip__normal-range__value', children: [min, " - ", max] })] });
    };
    render_Units = () => {
        const units = this.resolveMeasurementUnits();
        if (!units)
            return;
        return _jsxs(LL_H_C, { className: 'finding-tooltip__units', children: [_jsx("div", { className: 'finding-tooltip__units__title', children: "Units:" }), _jsx("div", { className: 'finding-tooltip__units__value', children: units })] });
    };
    render_Divider = () => {
        return _jsx("div", { className: 'finding-tooltip__divider' });
    };
}
//# sourceMappingURL=Tooltip_Finding_Base.js.map